import React, { Fragment, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { isEmpty } from "ramda";
import * as S from "sanctuary";
import {
  Box,
  ButtonWithAction,
  Center,
  Cluster,
  Heading,
  Text,
  NoResultsIcon,
  Paragraph,
  constants,
  SortableTableHeading,
} from "@thecb/components";

import {
  ERRORED,
  getValueSafely,
  hasAllAgencies,
  toFormattedRole,
  ALL_AGENCIES,
  PENDING,
  RESULTS_PER_PAGE,
  setSortState,
  SUCCESSFUL,
} from "./AdministeredUsers.state";
import { PageContent } from "@/components/generic/PageLayout";
import {
  TableContainer,
  TableWrapper,
  StatusColumn,
  SpinnerWrapper,
  SpinnerOverlay,
} from "./AdministeredUsersTable.styled";
import connectUserPermissions from "@/components/currentUser/CurrentUserPermissions";
import CheckIcon from "@/components/generic/CheckIcon";
import CloseIcon from "@/components/generic/CloseIcon";
import { makeFakeRow } from "@/components/generic/FakeRow";
import {
  Table,
  TableBody,
  TableHeader,
  TableHeading,
  TableRow,
  TableRowEntry,
} from "@/components/generic/NewTable";
import Spinner from "@/components/generic/Spinner";
import { TEXT_PRIMARY, WHITE } from "@/constants/colors";
import { SEMI_BOLD, TITLE } from "@/constants/fonts";
import { USER_PROFILE } from "@/constants/routes";
import StatusPill from "./subcomponents/status-pill/StatusPill";
import AgencyRow from "./subcomponents/agency-row/AgencyRow";
import AgenciesMenu from "./subcomponents/agencies-menu/AgenciesMenu";
import { NO_ROLE } from "../../sanctuary/typedefs";
import { noop } from "@/util/common";
import FilterToolbar from "./FilterToolbar";
import { ALL_PERMISSIONS } from "../currentUser/constants";
import { useDispatch } from "react-redux";
import MultiSelectFilterBar from "./MultiSelectFilterBar";
import { selectAppliedFilters } from "./AdministeredUsers.state";

const FakeRow = makeFakeRow([90, 130, 180, 90, 90, 55]);
const FakeRowSecondary = makeFakeRow([120, 150, 230, 120, 120, 90]);

const UserRow =
  ({
    currentUserAgencies,
    currentUserId,
    isPOSEnabled,
    modificationStatuses,
    openEditUserWorkflow,
    redirectToProfilePage,
    userCanGrantAgencyMembershipToUser,
    appliedFilters,
    expandedRowIds,
    onToggleRowExpanded,
  }) =>
  (
    { agencies, email, firstName, id, lastName, pos_role, role, statusLabel },
    index
  ) => {
    // Override agency column values if filters are applied
    const safeAgencies =
      appliedFilters?.agencies?.length > 0
        ? appliedFilters.agencies
        : S.fromMaybe([])(agencies);
    // User statuses are displayed in the <StatusPill> component referenced within this component.
    const userStatus = S.fromMaybe("")(statusLabel);
    const safeCurrentUserAgencies = S.fromMaybe([])(currentUserAgencies);
    const safeId = S.fromMaybe("")(id);
    const safeFirstName = S.fromMaybe("")(firstName);
    const safeLastName = S.fromMaybe("")(lastName);
    const safeEmail = S.fromMaybe("unknown")(email);
    const safeRole = S.fromMaybe(S.Right(""))(role);
    const safePOSRole = S.fromMaybe(S.Right(NO_ROLE))(pos_role);

    const rowKey = safeId || safeEmail || String(index);
    const isExpanded = Boolean(expandedRowIds?.[rowKey]);
    const onToggleExpanded = () => onToggleRowExpanded(rowKey);

    const matchingAgencies = safeAgencies.reduce(
      (acc, curr) =>
        !isEmpty(safeCurrentUserAgencies)
          ? safeCurrentUserAgencies.find((item) => {
              if (appliedFilters?.agencies?.length > 0) {
                const currentUserName = item?.name ?? "";
                const name = curr?.value ?? "";
                return currentUserName === name;
              } else {
                const slug = item?.slug ?? "";
                const currentUserSlug = curr?.slug ?? "";
                return slug === currentUserSlug;
              }
            })
            ? [...acc, curr.name]
            : acc
          : acc,
      []
    );

    const safeStatus = modificationStatuses[safeId]
      ? modificationStatuses[safeId].status
      : SUCCESSFUL;
    const userModificationDidError = safeStatus === ERRORED;
    const userIsUpdating = safeStatus === PENDING;

    const ModificationResultIcon = userModificationDidError
      ? () => <CloseIcon color={WHITE} />
      : CheckIcon;

    const isCurrentUsersRow = currentUserId === safeId;
    const onRowClick = isCurrentUsersRow
      ? redirectToProfilePage
      : userCanGrantAgencyMembershipToUser
        ? () => openEditUserWorkflow(safeId, safeRole)
        : noop;

    return (
      <TableRow key={safeId || index} onClick={onRowClick}>
        <TableRowEntry
          maxWidth={"180px"}
        >{`${safeLastName}, ${safeFirstName}`}</TableRowEntry>
        <TableRowEntry minWidth={"150px"} maxWidth={"290px"}>
          {safeEmail}
        </TableRowEntry>
        <AgencyRow
          maxWidth={246}
          hasAllAgencies={hasAllAgencies(
            safeRole,
            safePOSRole,
            S.fromMaybe([])(agencies)
          )}
          agencies={matchingAgencies}
          isExpanded={isExpanded}
          onToggleExpanded={onToggleExpanded}
        ></AgencyRow>
        <TableRowEntry extraStyles={"width: 200px;"}>
          {toFormattedRole(role)}
        </TableRowEntry>
        {isPOSEnabled && (
          <TableRowEntry extraStyles={"width: 200px;"}>
            {toFormattedRole(pos_role)}
          </TableRowEntry>
        )}
        <TableRowEntry maxWidth={"200px"}>
          <StatusColumn>
            <StatusPill>{userStatus}</StatusPill>
          </StatusColumn>
        </TableRowEntry>
        <SpinnerOverlay isHidden={!userIsUpdating}>
          <SpinnerWrapper>
            {userIsUpdating ? <Spinner /> : <ModificationResultIcon />}
          </SpinnerWrapper>
        </SpinnerOverlay>
      </TableRow>
    );
  };

const arePropsEqual = (prevProps, nextProps) => {
  /**
   * This could get expensive but is necessary because the connected form state props from Nested.js (`forms` and `alertBars`),
   * are passed in as different objects that are equal by value.
   *
   * This was causing the Search component rendered within `FilterToolbar` to trigger renders of the entire
   * AdministeredUsersTable table on keypress, making those events take > 300ms.
   *
   * To avoid re-rendering the entire user's table when a keypress occurs insde of the Search box, we can do
   * a stringified comparison of these as the memo callback function.
   */
  return JSON.stringify(prevProps) === JSON.stringify(nextProps);
};

const tableHeadingConfig = [
  { displayName: "Name", sortKey: "fullNameFormatted" },
  { displayName: "Email Address", sortKey: "email" },
  { displayName: "Agencies", sortKey: "" }, // Not sortable
  { displayName: "Revenue Management Role", sortKey: "role" },
  { displayName: "Cashiering Role", sortKey: "pos_role" },
  { displayName: "Status", sortKey: "statusLabel" },
];

const filterPOSData = (tableHeadingConfig, isPOSEnabled) =>
  tableHeadingConfig.filter((heading) => {
    if (heading.sortKey === "pos_role") {
      return isPOSEnabled;
    }
    return true;
  });

const AdministeredUsersTable = React.memo(
  ({
    administeredUsers,
    currentUserAgencies,
    currentUserId,
    currentUserRole,
    modificationStatuses,
    openEditUserWorkflow,
    openPersonalInfoWorkflow,
    requestAdministeredUsersPending,
    userCanGrantAgencyMembershipToUser,
    isPOSEnabled,
    sortState,
    userPermissions,
  }) => {
    const canViewUsers = userPermissions.value.includes(
      ALL_PERMISSIONS.userCanViewAdministeredUsers
    );
    const history = useHistory();
    const dispatch = useDispatch();
    const redirectToProfilePage = () => {
      history.push({ pathname: USER_PROFILE });
    };
    const appliedFilters = useSelector(selectAppliedFilters);
    const [expandedRowIds, setExpandedRowIds] = useState({});

    const isAllExpanded =
      administeredUsers.length > 0 &&
      administeredUsers.every((user, index) => {
        const safeId = S.fromMaybe("")(user.id);
        const safeEmail = S.fromMaybe("")(user.email);
        const rowKey = safeId || safeEmail || String(index);
        return Boolean(expandedRowIds[rowKey]);
      });

    const handleToggleAll = () => {
      const updatedRows = {};
      administeredUsers.forEach((user, index) => {
        const safeId = S.fromMaybe("")(user.id);
        const safeEmail = S.fromMaybe("")(user.email);
        const rowKey = safeId || safeEmail || String(index);
        updatedRows[rowKey] = !isAllExpanded;
      });
      setExpandedRowIds((prev) => ({
        ...prev,
        ...updatedRows,
      }));
    };

    const handleToggleRowExpanded = (rowKey) => {
      setExpandedRowIds((prev) => ({
        ...prev,
        [rowKey]: !prev[rowKey],
      }));
    };

    const downloadUsersToCSV = () => {
      let csvContent = "data:text/csv;charset=utf-8,";
      csvContent +=
        filterPOSData(tableHeadingConfig, isPOSEnabled)
          .map((th) => (th.displayName === "Agencies" ? "Agency" : th.displayName))
          .join(", ") + "\n";

      const getAgencyRows = (user) => {
        const agencies = getValueSafely(user, "agencies") || [];
        const agencyFilters = appliedFilters?.agencies ?? [];
        if (agencies === ALL_AGENCIES) {
          return [{ name: ALL_AGENCIES }];
        }
        // Override values if agency filters are applied
        if (agencyFilters.length) {
          return agencyFilters;
        }
        if (agencies.length) {
          return agencies;
        }
        return [null];
      };

      // Add user data
      administeredUsers.forEach((user) => {
        const agencyRows = getAgencyRows(user);

        // Create multiple rows of the same user for each agency they are assigned to
        agencyRows.forEach((agency) => {
          const rowValues = filterPOSData(tableHeadingConfig, isPOSEnabled).map(
            (th) => {
              const value =
                th.displayName === "Agencies"
                  ? agency?.name || ""
                  : getValueSafely(user, th.sortKey) || "";
              // Escape commas for fullNameFormatted field that is formatted as "Last Name, First Name"
              const escapedCommas = /[,"\n]/.test(value)
                ? `"${value.replace(/"/g, '""')}"`
                : value;
              // Replace all curly apostrophes (’) with straight apostrophes ('). Curly apostrophes can create encoding problems when exporting to CSV.
              const result = escapedCommas.replace(/’/g, "'");
              return result;
            }
          );
          csvContent += rowValues.join(", ") + "\n";
        });
      });

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `users.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    return (
      <TableContainer>
        <PageContent>
          <Box padding="0 0 1rem 0">
            <Heading
              variant="h5"
              color={TEXT_PRIMARY}
              weight={SEMI_BOLD}
              extraStyles={`font-family: ${TITLE}`}
            >
              User Settings
            </Heading>
            {canViewUsers && (
              <Cluster
                justify="space-between"
                align="flex-start"
                extraStyles="overflow: visible;"
              >
                <FilterToolbar />
                <Box padding="0" extraStyles="margin-right: 0.5rem;">
                  <ButtonWithAction
                    text={`Download ${administeredUsers.length || ""} User${
                      administeredUsers.length === 1 ? "" : "s"
                    }`}
                    action={downloadUsersToCSV}
                    disabled={
                      requestAdministeredUsersPending ||
                      administeredUsers.length === 0
                    }
                    variant="secondary"
                    dataQa="Download users"
                    extraStyles={`padding: 0.75rem 1.5rem; border-radius: 4px; margin: 0.25rem 0.5rem 0 0;`}
                  />
                  {userCanGrantAgencyMembershipToUser && (
                    <ButtonWithAction
                      text="Add new user"
                      action={openPersonalInfoWorkflow}
                      isLoading={false}
                      variant="primary"
                      dataQa="Add new user"
                      extraStyles={`padding: 0.75rem 1.5rem; border-radius: 4px; margin: 0.25rem 0 0;`}
                    />
                  )}
                </Box>
              </Cluster>
            )}
            <MultiSelectFilterBar />
          </Box>
          <TableWrapper>
            <Table id="users-table">
              <TableHeader>
                {filterPOSData(tableHeadingConfig, isPOSEnabled).map((heading) =>
                  heading.sortKey ? (
                    <SortableTableHeading
                      disabled={requestAdministeredUsersPending}
                      extraStyles={`
                        > div > div {
                          flex-wrap: nowrap;
                          gap: 0.5rem;
                          ${
                            heading.sortKey === "status" &&
                            `justify-content: flex-end; gap: 1rem;`
                          }
                        }
                        &:last-child {
                          text-align: right;
                        }
                      `}
                      ariaControlsId="users-table"
                      displayName={heading.displayName}
                      key={heading.displayName}
                      onSortChange={(_event) => {
                        // If switching columns default to ascending sort, otherwise toggle
                        const isSameColumn = sortState.column === heading.sortKey;
                        const order = isSameColumn
                          ? sortState.order === "asc"
                            ? "desc"
                            : "asc"
                          : "asc";
                        dispatch(setSortState(heading.sortKey, order));
                      }}
                      sortOrder={
                        sortState.column === heading.sortKey ? sortState.order : null
                      }
                    />
                  ) : heading.displayName === "Agencies" ? (
                    <TableHeading key={heading.displayName}>
                      <Cluster
                        justify="space-between"
                        align="center"
                        nowrap
                        overflow
                      >
                        {heading.displayName}
                        <AgenciesMenu
                          isAllExpanded={isAllExpanded}
                          onToggleAll={handleToggleAll}
                          disabled={
                            requestAdministeredUsersPending ||
                            administeredUsers.length === 0
                          }
                        />
                      </Cluster>
                    </TableHeading>
                  ) : (
                    <TableHeading key={heading.displayName}>
                      {heading.displayName}
                    </TableHeading>
                  )
                )}
              </TableHeader>
              <TableBody extraStyles={{ backgroundColor: WHITE }}>
                {requestAdministeredUsersPending
                  ? Array(RESULTS_PER_PAGE / 2)
                      .fill()
                      .map((_, index) => (
                        <Fragment key={index}>
                          {FakeRow}
                          {FakeRowSecondary}
                        </Fragment>
                      ))
                  : administeredUsers.map(
                      UserRow({
                        currentUserId,
                        modificationStatuses,
                        userCanGrantAgencyMembershipToUser,
                        openEditUserWorkflow,
                        redirectToProfilePage,
                        currentUserRole,
                        currentUserAgencies,
                        isPOSEnabled,
                        appliedFilters,
                        expandedRowIds,
                        onToggleRowExpanded: handleToggleRowExpanded,
                      })
                    )}
              </TableBody>
            </Table>
            {administeredUsers.length === 0 && !requestAdministeredUsersPending && (
              <Box padding="64px" background={WHITE}>
                <Center intrinsic>
                  {!canViewUsers ? (
                    <>
                      <NoResultsIcon
                        fillPrimary={constants.colors.LUCKY_POINT}
                        fillSecondary={constants.colors.HAWKES_BLUE}
                      />
                      <Heading
                        variant="h3"
                        weight="700"
                        extraStyles="padding-top: 32px;"
                      >
                        Nothing to See Here
                      </Heading>
                      <Text variant="pL">
                        Your role doesn&apos;t allow you to see the user list.
                      </Text>
                    </>
                  ) : (
                    <>
                      <NoResultsIcon
                        fillPrimary={constants.colors.LUCKY_POINT}
                        fillSecondary={constants.colors.HAWKES_BLUE}
                      />
                      <Heading variant="h3" weight="700">
                        No Results Found
                      </Heading>
                      <Paragraph variant="p" extraStyles={`text-align: center;`}>
                        We couldn&apos;t find anything that matched your search.{" "}
                        <br />
                        Please edit your search and try again.
                      </Paragraph>
                    </>
                  )}
                </Center>
              </Box>
            )}
          </TableWrapper>
        </PageContent>
      </TableContainer>
    );
  },
  arePropsEqual
);

export { AdministeredUsersTable };
export default connectUserPermissions(AdministeredUsersTable);
